import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Badge } from "../Badge";
import { Search, Plus, X, Send } from "lucide-react";

export const CAREGIVER_STATUSES = ["Active", "Available", "Assigned", "Unavailable", "Inactive"];

const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal";

const emptyForm = { name: "", email: "", phone: "", status: "Available", availability: "", create_login: false, password: "" };

export default function Caregivers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', id }
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/caregivers").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = [...items];
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((c) => [c.name, c.email, c.phone].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    if (statusFilter) list = list.filter((c) => c.status === statusFilter);
    return list;
  }, [items, q, statusFilter]);

  const openAdd = () => {
    setForm(emptyForm);
    setDetail(null);
    setModal({ mode: "add" });
  };

  const openEdit = async (c) => {
    setForm({ name: c.name, email: c.email, phone: c.phone || "", status: c.status, availability: c.availability || "", create_login: false, password: "" });
    setModal({ mode: "edit", id: c.id });
    setNote("");
    try {
      const res = await api.get(`/admin/caregivers/${c.id}`);
      setDetail(res.data);
    } catch {
      setDetail(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "add") {
        await api.post("/admin/caregivers", form);
        toast.success("Caregiver added.");
      } else {
        await api.patch(`/admin/caregivers/${modal.id}`, { name: form.name, phone: form.phone, status: form.status, availability: form.availability });
        toast.success("Caregiver updated.");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/admin/caregivers/${modal.id}/notes`, { text: note });
      setNote("");
      const res = await api.get(`/admin/caregivers/${modal.id}`);
      setDetail(res.data);
      toast.success("Note added.");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!items) return <p className="text-slate-500 font-semibold">Loading caregivers…</p>;

  return (
    <div data-testid="admin-caregivers">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Caregivers</h1>
          <p className="mt-2 text-[15px] text-slate-600">Manage your caregiving team, their availability, and portal access.</p>
        </div>
        <button onClick={openAdd} data-testid="add-caregiver-button" className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
          <Plus className="h-4 w-4" /> Add Caregiver
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="caregiver-search" type="text" placeholder="Search caregivers…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal" />
        </div>
        <select data-testid="caregiver-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
          <option value="">All statuses</option>
          {CAREGIVER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <button key={c.id} data-testid={`caregiver-card-${c.id}`} onClick={() => openEdit(c)} className="text-left rounded-2xl border border-slate-200 bg-white p-6 hover:border-brand-teal/40 hover:shadow-[0_16px_40px_rgb(11,27,61,0.08)] transition-shadow duration-300">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-display text-xl text-brand-navy">{c.name}</h2>
              <Badge kind="caregiver" value={c.status} />
            </div>
            <p className="mt-2 text-sm text-slate-600">{c.email}</p>
            <p className="text-sm text-slate-600">{c.phone || "No phone on file"}</p>
            <p className="mt-2 text-xs text-slate-500"><strong>Availability:</strong> {c.availability || "Not set"}</p>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 rounded-2xl border border-slate-200 bg-white p-14 text-center text-slate-500">No caregivers found.</div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/60 p-4" onClick={() => setModal(null)}>
          <div data-testid="caregiver-modal" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-brand-navy">{modal.mode === "add" ? "Add Caregiver" : "Edit Caregiver"}</h2>
              <button onClick={() => setModal(null)} data-testid="caregiver-modal-close" aria-label="Close" className="p-2 rounded-lg hover:bg-brand-mist"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="cg-name" className="block text-sm font-bold text-brand-navy">Full Name *</label>
                <input id="cg-name" data-testid="cg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="cg-email" className="block text-sm font-bold text-brand-navy">Email *</label>
                <input id="cg-email" data-testid="cg-email" type="email" required disabled={modal.mode === "edit"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`mt-2 ${inputCls} disabled:bg-slate-100`} />
              </div>
              <div>
                <label htmlFor="cg-phone" className="block text-sm font-bold text-brand-navy">Phone</label>
                <input id="cg-phone" data-testid="cg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="cg-status" className="block text-sm font-bold text-brand-navy">Status</label>
                <select id="cg-status" data-testid="cg-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`mt-2 ${inputCls}`}>
                  {CAREGIVER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cg-availability" className="block text-sm font-bold text-brand-navy">Availability</label>
                <input id="cg-availability" data-testid="cg-availability" placeholder="e.g. Mon–Fri, 9 AM – 5 PM" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              {modal.mode === "add" && (
                <div className="sm:col-span-2 rounded-xl border border-slate-200 p-4">
                  <label className="flex items-center gap-2.5 text-sm font-bold text-brand-navy cursor-pointer">
                    <input type="checkbox" data-testid="cg-create-login" checked={form.create_login} onChange={(e) => setForm({ ...form, create_login: e.target.checked })} className="h-4 w-4 accent-[#008080]" />
                    Create Caregiver Portal login for this caregiver
                  </label>
                  {form.create_login && (
                    <div className="mt-3">
                      <label htmlFor="cg-password" className="block text-sm font-bold text-brand-navy">Temporary Password (min 8 characters)</label>
                      <input id="cg-password" data-testid="cg-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`mt-2 ${inputCls}`} />
                      <p className="mt-1.5 text-xs text-slate-500">Share this with the caregiver securely. They can reset it via Forgot Password.</p>
                    </div>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <button type="submit" data-testid="cg-save" disabled={saving} className="rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60">
                  {saving ? "Saving…" : modal.mode === "add" ? "Add Caregiver" : "Save Changes"}
                </button>
              </div>
            </form>

            {modal.mode === "edit" && detail && (
              <div className="mt-6 pt-6 border-t border-slate-100 space-y-5">
                <div>
                  <p className="text-sm font-bold text-brand-navy">Portal access: {detail.has_login ? "Enabled" : "No login created"}</p>
                  <p className="mt-1 text-sm text-slate-600"><strong>Assigned patients:</strong> {(detail.assigned_patients || []).length ? detail.assigned_patients.join(", ") : "None"}</p>
                  <p className="mt-1 text-sm text-slate-600"><strong>Assigned visits:</strong> {(detail.visits || []).length}</p>
                </div>
                <div>
                  <h3 className="font-display text-lg text-brand-navy">Internal Notes</h3>
                  <ul className="mt-3 space-y-2">
                    {(detail.internal_notes || []).length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
                    {(detail.internal_notes || []).map((n) => (
                      <li key={n.id} className="rounded-xl bg-brand-mist/60 px-4 py-3">
                        <p className="text-sm text-brand-navy">{n.text}</p>
                        <p className="mt-1 text-xs text-slate-500">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                      </li>
                    ))}
                  </ul>
                  <form onSubmit={addNote} className="mt-3 flex gap-3">
                    <input data-testid="cg-note-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" className={`flex-1 ${inputCls}`} />
                    <button type="submit" data-testid="cg-note-add" className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-navy"><Send className="h-4 w-4" /></button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
